import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  Infinity as InfinityIcon,
  LockKeyhole,
  PencilLine,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Table2,
  UploadCloud,
  Users,
  WandSparkles,
  Zap,
} from 'lucide-react';
import { landingConfig } from '../config/landingConfig';

const CMS_API_URL = import.meta.env.VITE_STATEMENTSYNC_CMS_API_URL;
const APP_ID = import.meta.env.VITE_STATEMENTSYNC_APP_ID || 'statementsync_pro';

const FALLBACK_PLANS = [
  {
    id: 'monthly',
    name: 'Monthly Plan',
    price: 'A$24.99',
    period: '/ month',
    deviceAccess: '1 Device Access',
    note: 'Flexible access for individual use.',
    badge: '',
    highlight: false,
    linkType: 'monthly',
  },
  {
    id: 'annual',
    name: 'Annual Plan',
    price: 'A$147.99',
    period: '/ year',
    deviceAccess: '2 Devices',
    note: 'Best fit for regular statement processing.',
    badge: 'Popular',
    highlight: true,
    linkType: 'annual',
  },
  {
    id: 'lifetime',
    name: 'Lifetime Plan',
    price: 'A$187.99',
    period: '/ year',
    deviceAccess: '3 Devices Access',
    note: 'More device access for broader use.',
    badge: '',
    highlight: false,
    linkType: 'lifetime',
  },
];

const CORE_FEATURES = [
  'Unlimited bank statements or transactions',
  'Under 30 second conversion workflow',
  'Excel and CSV export',
  'Review and edit before export',
  '100% private processing',
];

const DEMO_ROWS = [
  ['01/03/2024', 'Coffee Shop', '-$4.50'],
  ['02/03/2024', 'Supplier Payment', '-$123.45'],
  ['04/03/2024', 'Client Receipt', '$2,500.00'],
  ['06/03/2024', 'Fuel', '-$80.00'],
];

const WORKFLOW = [
  {
    icon: UploadCloud,
    title: 'Drop in a readable PDF',
    copy: 'Start with a readable bank statement PDF rather than manually copying rows into a spreadsheet.',
  },
  {
    icon: ScanLine,
    title: 'Transactions are extracted',
    copy: 'StatementSync identifies dates, descriptions and amounts and turns them into structured rows.',
  },
  {
    icon: PencilLine,
    title: 'Review before you trust it',
    copy: 'Check and edit the extracted rows in a clean review grid before anything is exported.',
  },
  {
    icon: Download,
    title: 'Export Excel or CSV',
    copy: 'Download clean spreadsheet-ready data for bookkeeping, reconciliation, analysis or record keeping.',
  },
];

const AUDIENCES = [
  ['Bookkeepers', 'Client PDFs to structured rows'],
  ['Accountants', 'Cleaner data before import'],
  ['Small businesses', 'Less repetitive data entry'],
  ['Property investors', 'Faster expense review'],
  ['Admin teams', 'Cleaner monthly processing'],
  ['Individuals', 'Old statements made usable'],
];

const FEATURE_CARDS = [
  {
    icon: InfinityIcon,
    title: 'Unlimited conversions',
    copy: 'Process as many supported bank statements or transactions as your workflow requires.',
  },
  {
    icon: Clock3,
    title: 'Built for speed',
    copy: 'A typical conversion workflow is designed to take under 30 seconds for readable statements.',
  },
  {
    icon: ShieldCheck,
    title: '100% private',
    copy: 'Your statement information is not transmitted anywhere during the conversion workflow.',
  },
  {
    icon: WandSparkles,
    title: 'Multi-line cleanup',
    copy: 'Descriptions split across PDF lines are cleaned into more useful transaction rows where possible.',
  },
  {
    icon: Table2,
    title: 'Reviewable output',
    copy: 'Inspect and edit the extracted rows before you commit to the final spreadsheet file.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Excel + CSV',
    copy: 'Export into familiar spreadsheet formats that slot naturally into downstream workflows.',
  },
];

function hasValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function formatPrice(value, currency = 'A$') {
  if (!hasValue(value)) return '';
  const price = String(value).trim();
  return price.includes('$') ? price : `${currency || 'A$'}${price}`;
}

function useCmsPricing() {
  const [cmsData, setCmsData] = useState(null);
  const [status, setStatus] = useState(CMS_API_URL ? 'loading' : 'static');

  useEffect(() => {
    if (!CMS_API_URL) return undefined;

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    fetch(`${CMS_API_URL}?appId=${APP_ID}&_ts=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`CMS HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data?.success) {
          setCmsData(data);
          setStatus('live');
        } else {
          setStatus('fallback');
        }
      })
      .catch(() => setStatus('fallback'))
      .finally(() => window.clearTimeout(timeout));

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  const plans = useMemo(() => {
    return FALLBACK_PLANS.map((fallback) => {
      const live = (cmsData?.pricingPlans || []).find((plan) =>
        plan.planId === fallback.id ||
        plan.planType?.toLowerCase() === fallback.id ||
        plan.planName?.toLowerCase().includes(fallback.id)
      );

      return {
        ...fallback,
        name: hasValue(live?.planName) ? live.planName : fallback.name,
        price: hasValue(live?.price) ? formatPrice(live.price, live.currency) : fallback.price,
        period: hasValue(live?.billingText) ? live.billingText : fallback.period,
        note: hasValue(live?.footerNote || live?.description) ? (live.footerNote || live.description) : fallback.note,
        badge: hasValue(live?.badgeText) ? live.badgeText : fallback.badge,
        highlight: typeof live?.isMostPopular === 'boolean' ? live.isMostPopular : fallback.highlight,
        isActive: typeof live?.isActive === 'boolean' ? live.isActive : true,
        link: landingConfig.checkoutLinks[fallback.linkType],
      };
    }).filter((plan) => plan.isActive !== false);
  }, [cmsData]);

  return { plans, status };
}

function ProductDemo({ reducedMotion }) {
  const transition = reducedMotion ? { duration: 0 } : { duration: 0.55 };

  return (
    <div className="relative mx-auto w-full max-w-2xl">
      <div className="absolute -inset-8 rounded-[2.5rem] bg-gradient-to-br from-blue-200/50 via-cyan-100/40 to-emerald-100/60 blur-3xl" />
      <div className="relative overflow-hidden rounded-[2rem] border border-white/80 bg-white/90 p-4 shadow-[0_30px_80px_rgba(15,23,42,0.15)] backdrop-blur-xl sm:p-6">
        <div className="mb-5 flex items-center justify-between border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">
            Private processing
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-[0.8fr_auto_1.25fr] sm:items-center">
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, x: -18, rotate: -2 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={transition}
            className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
          >
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-500 text-white shadow-lg shadow-rose-500/20">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900">Bank statement</p>
                <p className="text-xs text-slate-500">statement.pdf</p>
              </div>
            </div>
            <div className="space-y-2.5">
              {[92, 72, 86, 58, 78].map((width, index) => (
                <motion.div
                  key={width}
                  initial={reducedMotion ? false : { scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ ...transition, delay: reducedMotion ? 0 : 0.08 * index }}
                  className="h-2 origin-left rounded-full bg-slate-200"
                  style={{ width: `${width}%` }}
                />
              ))}
            </div>
          </motion.div>

          <div className="flex items-center justify-center">
            <motion.div
              animate={reducedMotion ? {} : { x: [0, 6, 0] }}
              transition={reducedMotion ? {} : { repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white shadow-lg shadow-blue-500/20"
            >
              <ArrowRight className="h-5 w-5" />
            </motion.div>
          </div>

          <motion.div
            initial={reducedMotion ? false : { opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...transition, delay: reducedMotion ? 0 : 0.25 }}
            className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-lg shadow-blue-900/5"
          >
            <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-emerald-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                <span className="text-sm font-extrabold text-slate-900">Clean transactions</span>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-slate-500 shadow-sm">Excel / CSV</span>
            </div>
            <div className="grid grid-cols-[0.7fr_1.4fr_0.7fr] border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
              <span>Date</span><span>Description</span><span className="text-right">Amount</span>
            </div>
            <div className="divide-y divide-slate-100">
              {DEMO_ROWS.map((row, index) => (
                <motion.div
                  key={row[1]}
                  initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...transition, delay: reducedMotion ? 0 : 0.45 + index * 0.12 }}
                  className="grid grid-cols-[0.7fr_1.4fr_0.7fr] px-3 py-3 text-[11px] text-slate-600 sm:text-xs"
                >
                  <span>{row[0]}</span><span className="font-semibold text-slate-800">{row[1]}</span><span className="text-right font-semibold">{row[2]}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2">
          {[
            ['Unlimited', InfinityIcon],
            ['< 30 sec', Zap],
            ['100% private', LockKeyhole],
          ].map(([label, Icon]) => (
            <div key={label} className="flex items-center justify-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-2 py-3 text-center text-[10px] font-extrabold text-slate-700 sm:text-xs">
              <Icon className="h-4 w-4 text-blue-600" />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SalesLandingPage() {
  const reducedMotion = useReducedMotion();
  const { plans, status } = useCmsPricing();
  const [openFaq, setOpenFaq] = useState(null);
  const faqs = landingConfig.faqItems || [];

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });

  const reveal = {
    initial: reducedMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: reducedMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] },
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-sans text-slate-950 selection:bg-blue-200">
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-8">
          <button onClick={() => scrollTo('top')} className="flex items-center gap-3 text-left" aria-label="StatementSync home">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-600/20">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-black tracking-tight text-slate-950">Statement<span className="text-blue-600">Sync</span></div>
              <div className="hidden text-[9px] font-bold uppercase tracking-[0.22em] text-slate-400 sm:block">Bank statements, simplified</div>
            </div>
          </button>

          <div className="hidden items-center gap-7 lg:flex">
            <button onClick={() => scrollTo('how-it-works')} className="text-sm font-bold text-slate-500 transition hover:text-slate-950">How it works</button>
            <button onClick={() => scrollTo('privacy')} className="text-sm font-bold text-slate-500 transition hover:text-slate-950">Privacy</button>
            <button onClick={() => scrollTo('pricing')} className="text-sm font-bold text-slate-500 transition hover:text-slate-950">Pricing</button>
            <button onClick={() => scrollTo('faq')} className="text-sm font-bold text-slate-500 transition hover:text-slate-950">FAQs</button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <a href={landingConfig.existingUserAppUrl} className="hidden rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-100 sm:inline-flex">I have a licence</a>
            <button onClick={() => scrollTo('pricing')} className="inline-flex items-center rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-blue-700">
              View plans <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </nav>

      <main id="top">
        <section className="relative overflow-hidden pt-28 sm:pt-36 lg:pt-40">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.13),_transparent_35%),radial-gradient(circle_at_80%_20%,_rgba(16,185,129,0.13),_transparent_30%)]" />
          <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 sm:px-6 sm:pb-28 lg:grid-cols-[1.02fr_0.98fr] lg:gap-16 lg:px-8">
            <motion.div initial={reducedMotion ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.55 }} className="text-center lg:text-left">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50/80 px-4 py-2 text-xs font-extrabold text-blue-700 shadow-sm">
                <Sparkles className="h-4 w-4" /> PDF bank statements → clean spreadsheet data
              </div>
              <h1 className="text-4xl font-black leading-[1.05] tracking-[-0.04em] text-slate-950 sm:text-6xl lg:text-7xl">
                Convert PDF bank statements into <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">clean Excel or CSV.</span>
              </h1>
              <p className="mx-auto mt-7 max-w-2xl text-base font-medium leading-7 text-slate-600 sm:text-xl sm:leading-8 lg:mx-0">
                Turn readable bank statement PDFs into structured transaction rows in under 30 seconds, review the result, and export without sending your statement information anywhere.
              </p>

              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
                <button onClick={() => scrollTo('pricing')} className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-7 py-4 text-base font-black text-white shadow-xl shadow-blue-600/25 transition hover:-translate-y-1 hover:bg-blue-700">
                  Choose a plan <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button onClick={() => scrollTo('how-it-works')} className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-7 py-4 text-base font-black text-slate-800 shadow-sm transition hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg">
                  See how it works
                </button>
              </div>

              <div className="mt-9 grid grid-cols-3 gap-2 sm:max-w-xl sm:gap-3 lg:mx-0">
                {[
                  ['Unlimited', 'Statements / transactions'],
                  ['< 30 sec', 'Conversion workflow'],
                  ['100%', 'Private processing'],
                ].map(([value, label]) => (
                  <div key={value} className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-4 text-center shadow-sm backdrop-blur sm:px-4">
                    <div className="text-lg font-black text-slate-950 sm:text-2xl">{value}</div>
                    <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-400 sm:text-[10px]">{label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={reducedMotion ? false : { opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: reducedMotion ? 0 : 0.65, delay: reducedMotion ? 0 : 0.1 }}>
              <ProductDemo reducedMotion={reducedMotion} />
            </motion.div>
          </div>
        </section>

        <section className="border-y border-slate-100 bg-slate-50/70 py-8">
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-4 sm:grid-cols-4 sm:px-6 lg:px-8">
            {[
              ['Readable PDF', FileText],
              ['Automatic extraction', ScanLine],
              ['Review + edit', PencilLine],
              ['Excel + CSV', FileSpreadsheet],
            ].map(([label, Icon]) => (
              <div key={label} className="flex items-center justify-center gap-2 text-center text-xs font-extrabold text-slate-600 sm:text-sm">
                <Icon className="h-4 w-4 text-blue-600" /> {label}
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 sm:py-28">
          <motion.div {...reveal} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-16">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">Why it matters</span>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">Stop retyping financial data that already exists.</h2>
              <p className="mt-5 text-base leading-7 text-slate-600 sm:text-lg">StatementSync is purpose-built for the awkward gap between a bank statement PDF and the spreadsheet data you actually need.</p>
            </div>

            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-[2rem] border border-rose-100 bg-rose-50/60 p-7 sm:p-9">
                <div className="mb-6 inline-flex rounded-xl bg-rose-100 px-3 py-1.5 text-xs font-black uppercase tracking-wider text-rose-700">The old way</div>
                <div className="space-y-5">
                  {[
                    'Copy and paste breaks transaction descriptions across rows.',
                    'Manual retyping introduces mistakes and consumes valuable time.',
                    'Raw PDF layouts are difficult to filter, sort or reuse.',
                  ].map((item) => (
                    <div key={item} className="flex gap-3 text-sm font-semibold leading-6 text-slate-700 sm:text-base">
                      <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-rose-500 shadow-sm">×</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[2rem] border border-blue-100 bg-gradient-to-br from-blue-50 to-emerald-50 p-7 sm:p-9">
                <div className="mb-6 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-xs font-black uppercase tracking-wider text-blue-700 shadow-sm"><Zap className="h-4 w-4" /> The StatementSync way</div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    ['Fast', 'Structured data in under 30 seconds for readable statements.'],
                    ['Private', 'No statement information is transmitted anywhere.'],
                    ['Controlled', 'Review and edit the rows before export.'],
                    ['Reusable', 'Download clean Excel or CSV files.'],
                  ].map(([title, copy]) => (
                    <div key={title} className="rounded-2xl border border-white/80 bg-white/80 p-5 shadow-sm">
                      <div className="mb-2 flex items-center gap-2 font-black text-slate-950"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> {title}</div>
                      <p className="text-sm leading-6 text-slate-600">{copy}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        <section id="how-it-works" className="overflow-hidden bg-slate-950 py-20 text-white sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="mx-auto mb-14 max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">One clear workflow</span>
              <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-5xl">From PDF to spreadsheet-ready data.</h2>
              <p className="mt-5 text-base leading-7 text-slate-300 sm:text-lg">Each step exists for a reason: extract quickly, make the result visible, let you check it, then export it cleanly.</p>
            </motion.div>

            <div className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              <motion.div
                initial={reducedMotion ? false : { scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: reducedMotion ? 0 : 1.2, ease: 'easeOut' }}
                className="absolute left-[8%] right-[8%] top-10 hidden h-px origin-left bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 lg:block"
              />
              {WORKFLOW.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div key={step.title} {...reveal} transition={{ duration: reducedMotion ? 0 : 0.45, delay: reducedMotion ? 0 : index * 0.08 }} className="relative rounded-[1.75rem] border border-slate-800 bg-slate-900 p-6 shadow-2xl shadow-black/10">
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg shadow-blue-600/20"><Icon className="h-6 w-6" /></div>
                      <span className="text-4xl font-black text-slate-800">0{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-black">{step.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-400">{step.copy}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="privacy" className="relative overflow-hidden py-20 sm:py-28">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_50%,_rgba(16,185,129,0.13),_transparent_32%),radial-gradient(circle_at_85%_50%,_rgba(59,130,246,0.11),_transparent_30%)]" />
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <motion.div {...reveal}>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700"><LockKeyhole className="h-4 w-4" /> Privacy is a product feature</div>
              <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">Your financial data stays with you.</h2>
              <p className="mt-6 text-base leading-7 text-slate-600 sm:text-lg">StatementSync is designed so your bank statement information is not transmitted anywhere during the conversion workflow. That means less exposure for highly sensitive financial data.</p>
              <div className="mt-8 space-y-4">
                {[
                  'No bank statement information transmitted anywhere',
                  'Review the extracted rows before export',
                  'No need to email statements to a third party',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 font-bold text-slate-700"><BadgeCheck className="h-5 w-5 text-emerald-500" /> {item}</div>
                ))}
              </div>
            </motion.div>

            <motion.div {...reveal} className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-2xl shadow-slate-950/15 sm:p-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-300">Private workflow</p>
                  <p className="mt-2 text-2xl font-black">Nothing leaves the boundary.</p>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-300"><ShieldCheck className="h-7 w-7" /></div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                <div className="relative mx-auto flex min-h-48 max-w-md items-center justify-center rounded-2xl border border-dashed border-emerald-400/40 bg-emerald-400/5 p-6">
                  <motion.div animate={reducedMotion ? {} : { scale: [1, 1.04, 1] }} transition={reducedMotion ? {} : { repeat: Infinity, duration: 2.4 }} className="flex flex-col items-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-400 text-slate-950"><LockKeyhole className="h-8 w-8" /></div>
                    <p className="mt-4 font-black">Your device</p>
                    <p className="mt-1 text-xs text-slate-400">PDF → review → Excel / CSV</p>
                  </motion.div>
                </div>
                <p className="mt-4 text-center text-xs font-bold uppercase tracking-[0.16em] text-slate-500">No outward data path</p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-slate-50 py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="mx-auto mb-14 max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">Built around the job</span>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">Useful features, not feature clutter.</h2>
            </motion.div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURE_CARDS.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div key={feature.title} {...reveal} transition={{ duration: reducedMotion ? 0 : 0.45, delay: reducedMotion ? 0 : index * 0.04 }} whileHover={reducedMotion ? {} : { y: -6 }} className="group rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-xl hover:shadow-slate-900/5 sm:p-7">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white"><Icon className="h-6 w-6" /></div>
                    <h3 className="text-lg font-black text-slate-950">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{feature.copy}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
              <div>
                <span className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">Who it is for</span>
                <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">A practical tool for anyone stuck with PDF statements.</h2>
                <p className="mt-5 text-base leading-7 text-slate-600 sm:text-lg">Whether the next step is bookkeeping, reconciliation, budgeting or record cleanup, the goal is the same: get usable rows without retyping them.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {AUDIENCES.map(([title, copy], index) => (
                  <motion.div key={title} whileHover={reducedMotion ? {} : { x: 4 }} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">{index < 3 ? <Users className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}</div>
                    <div><h3 className="font-black text-slate-950">{title}</h3><p className="mt-1 text-sm text-slate-500">{copy}</p></div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section id="pricing" className="relative overflow-hidden bg-slate-950 py-20 text-white sm:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(37,99,235,0.28),_transparent_35%)]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="mx-auto mb-14 max-w-3xl text-center">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">Simple plans</span>
              <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-5xl">Choose the access level that fits your workflow.</h2>
              <p className="mt-5 text-base leading-7 text-slate-300 sm:text-lg">Every plan includes the core StatementSync workflow. The purchase buttons always use the verified checkout destinations.</p>
              {status === 'live' && <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-400/10 px-3 py-1.5 text-xs font-bold text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Live pricing synced</div>}
            </motion.div>

            <motion.div
              {...reveal}
              className="mb-8 overflow-hidden rounded-[2rem] border border-cyan-400/25 bg-gradient-to-br from-blue-600/20 via-slate-900 to-emerald-500/10 p-1 shadow-2xl shadow-blue-950/20"
            >
              <div className="grid gap-6 rounded-[1.75rem] border border-white/10 bg-slate-900/90 p-6 sm:p-8 lg:grid-cols-[1.35fr_0.65fr] lg:items-center">
                <div>
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-cyan-200">
                    <Sparkles className="h-4 w-4" /> Try StatementSync free
                  </div>
                  <h3 className="text-2xl font-black text-white sm:text-3xl">No account. No credit card. Start with your own statements.</h3>
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
                    Trial mode lets you process unlimited supported statements and experience the real workflow. Each trial export is limited to the first 10 extracted transactions from that statement.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {['No account required', 'No credit card required', 'Unlimited statement trials', 'First 10 transactions exported'].map((item) => (
                      <span key={item} className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-bold text-slate-200">{item}</span>
                    ))}
                  </div>
                </div>
                <div className="lg:text-right">
                  <a
                    href={landingConfig.freeTrialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-white px-6 py-4 text-sm font-black text-slate-950 shadow-xl shadow-black/20 transition hover:-translate-y-0.5 hover:bg-cyan-50 lg:w-auto"
                  >
                    Try it free <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                  <p className="mt-3 text-xs leading-5 text-slate-500">Free trial access does not require an account, a credit card or a purchase.</p>
                </div>
              </div>
            </motion.div>

            <div className="grid gap-6 lg:grid-cols-3">
              {plans.map((plan, index) => (
                <motion.div key={plan.id} {...reveal} transition={{ duration: reducedMotion ? 0 : 0.5, delay: reducedMotion ? 0 : index * 0.08 }} whileHover={reducedMotion ? {} : { y: -8 }} className={`relative flex flex-col rounded-[2rem] p-7 sm:p-8 ${plan.highlight ? 'border-2 border-blue-400 bg-white text-slate-950 shadow-2xl shadow-blue-500/15' : 'border border-slate-800 bg-slate-900 text-white'}`}>
                  {plan.badge && <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-blue-500 px-4 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow-lg">{plan.badge}</div>}
                  <div className="mb-7">
                    <p className={`text-xs font-black uppercase tracking-[0.18em] ${plan.highlight ? 'text-blue-600' : 'text-cyan-300'}`}>{plan.deviceAccess}</p>
                    <h3 className="mt-3 text-2xl font-black">{plan.name}</h3>
                    <p className={`mt-2 min-h-10 text-sm leading-6 ${plan.highlight ? 'text-slate-500' : 'text-slate-400'}`}>{plan.note}</p>
                  </div>
                  <div className="mb-7 flex items-baseline gap-2">
                    <span className="text-4xl font-black sm:text-5xl">{plan.price}</span>
                    <span className={`text-sm font-bold ${plan.highlight ? 'text-slate-500' : 'text-slate-400'}`}>{plan.period}</span>
                  </div>
                  <div className={`mb-8 h-px ${plan.highlight ? 'bg-slate-200' : 'bg-slate-800'}`} />
                  <ul className="mb-9 flex-1 space-y-4">
                    {CORE_FEATURES.map((feature) => (
                      <li key={feature} className={`flex items-start gap-3 text-sm font-semibold leading-5 ${plan.highlight ? 'text-slate-600' : 'text-slate-300'}`}>
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${plan.highlight ? 'text-blue-600' : 'text-emerald-400'}`} /> {feature}
                      </li>
                    ))}
                  </ul>
                  <a href={plan.link} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center rounded-2xl px-5 py-4 text-sm font-black transition hover:-translate-y-0.5 ${plan.highlight ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700' : 'bg-white text-slate-950 hover:bg-blue-50'}`}>
                    Choose {plan.name.replace('StatementSync ', '')} <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 text-center text-xs leading-6 text-slate-400 sm:text-sm">
              Core functionality is consistent across the plans. Device access and billing vary by plan. Checkout is completed securely through the payment provider.
            </div>
          </div>
        </section>

        <section id="faq" className="py-20 sm:py-28">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div {...reveal} className="mb-12 text-center">
              <span className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">Questions before you buy</span>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 sm:text-5xl">Straight answers.</h2>
            </motion.div>

            <div className="space-y-3">
              {faqs.slice(0, 8).map((faq, index) => (
                <div key={faq.question} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left sm:px-6" aria-expanded={openFaq === index}>
                    <span className="font-black text-slate-950">{faq.question}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === index && (
                      <motion.div initial={reducedMotion ? false : { height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={reducedMotion ? {} : { height: 0, opacity: 0 }} transition={{ duration: reducedMotion ? 0 : 0.25 }}>
                        <div className="border-t border-slate-100 px-5 py-5 text-sm leading-7 text-slate-600 sm:px-6">{faq.answer}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-slate-50 py-16">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-[1fr_auto] md:items-end lg:px-8">
            <div className="max-w-3xl">
              <div className="mb-3 flex items-center gap-2 text-sm font-black text-slate-950"><ShieldCheck className="h-5 w-5 text-emerald-500" /> About StatementSync</div>
              <p className="text-sm leading-7 text-slate-600">StatementSync is developed and supported by BPA Solutions in Australia. It turns readable bank statement PDFs into reviewable Excel and CSV transaction data for bookkeeping, reconciliation preparation, analysis and record cleanup.</p>
              <p className="mt-3 text-xs text-slate-400">Page updated <time dateTime="2026-09-29">29 September 2026</time>.</p>
            </div>
            <div className="text-sm leading-7 md:text-right">
              <p className="font-black text-slate-950">BPA Solutions</p>
              <a href={`mailto:${landingConfig.supportEmail}`} className="block font-semibold text-blue-600 hover:text-blue-700">{landingConfig.supportEmail}</a>
              <a href={`tel:${landingConfig.contactPhone.replace(/\s/g, '')}`} className="block font-semibold text-blue-600 hover:text-blue-700">{landingConfig.contactPhone}</a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}